import React, { useEffect, useCallback, useMemo } from "react";
import { connect, useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import Grid from "@material-ui/core/Grid";
import { useTranslation } from "react-i18next";
import FormControl from "@material-ui/core/FormControl";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons//Close";
import Checkbox from "@material-ui/core/Checkbox";
import Input from "@material-ui/core/Input";
import MenuItem from "@material-ui/core/MenuItem";
import ListItemText from "@material-ui/core/ListItemText";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import isEmpty from "lodash/isEmpty";
import get from "lodash/get";
import debounce from "lodash/debounce";
import { DateRange } from "@material-ui/icons";
import { Popover, Box } from "@material-ui/core";

import { SearchFilter } from "components/Filter";
import { AddressingKeyFilter } from "components/AddressingKey";
// Styles et sélecteur de plage de dates repris de l'écran Factures : les copies locales étaient
// identiques au caractère près. Une seule source à maintenir.
import useStyles from "pages/Invoices/List/style";
import DateRangePicker from "pages/Invoices/List/Components/DateRangePicker";
import { fetchAvoirs, fetchAvoirStatuses as fetchListStatus } from "redux/actions/Avoirs";
import { updateAvoirFilter as updateFilter, initialAvoirFilter as initialFilter } from "redux/avoirFilterReducer";

const columns = ["invoiceNo", "issueDate", "updatedAt", "supplier"];
const ISSUE_DATE = "issueDate";
const SAVE_DATE = "saveDate";
const CREATE_DATE = "createdDate";

function Filter(props) {
	const dispatch = useDispatch();
	const history = useHistory();
	const { fetchListStatus: loadAvoirStatuses, fromDashboard } = props;
	const [dateFilterAnchorEl, setDateFilterAnchorEl] = React.useState(null);
	const filterInvoices = useSelector(({ filterAvoirs: i }) => i);
	const statusList = Array.isArray(props.statusList) ? props.statusList : [];

	const classes = useStyles();
	const { t } = useTranslation();
	const [searchField, setSearchField] = React.useState("");
	const [sortBy, setSortBy] = React.useState("");

	const [startIssueDate, setStartIssueDate] = React.useState(null);
	const [endIssueDate, setEndIssueDate] = React.useState(null);
	const [startSaveDate, setStartSaveDate] = React.useState(null);
	const [endSaveDate, setEndSaveDate] = React.useState(null);
	const [startCreatedDate, setStartCreatedDate] = React.useState(null);
	const [endCreatedDate, setEndCreatedDate] = React.useState(null);
	const [selectedPeriodIntegration, setSelectedPeriodIntegration] = React.useState("");
	const [filterContext] = React.useState(
		history.location && history.location.state && history.location.state.filterContext
	);

	useEffect(() => {
		if (localStorage.getItem("avoir_filter")) {
			const storedFilter = JSON.parse(localStorage.getItem("avoir_filter"));
			dispatch(updateFilter(storedFilter));
		}
	}, [dispatch]);

	const onFilterChange = useCallback((newFilter) => {
		const filter = { ...filterInvoices, ...newFilter, pageNo: 0 };
		localStorage.setItem("avoir_filter", JSON.stringify(filter));
		dispatch(updateFilter(filter));

		if (fromDashboard) {
			dispatch(fetchAvoirs(filter));
		} else if (
			!newFilter ||
			(newFilter && newFilter.searchBy === null && Object.keys(newFilter).length > 0)
		) {
			dispatch(fetchAvoirs({ ...initialFilter, pageNo: filterContext?.pageNo }));
		}
	}, [dispatch, filterContext?.pageNo, filterInvoices, fromDashboard]);

	const handleStartDateChange = (date, type) => {
		if (type === ISSUE_DATE) {
			setStartIssueDate(date);
			onFilterChange({ startIssueDate: date });
		} else if (type === SAVE_DATE) {
			setStartSaveDate(date);
			onFilterChange({ startSaveDate: date });
		} else {
			setStartCreatedDate(date);
			onFilterChange({ startCreatedDate: date });
		}
	};

	const handleEndDateChange = (date, type) => {
		if (type === ISSUE_DATE) {
			setEndIssueDate(date);
			onFilterChange({ endIssueDate: date });
		} else if (type === SAVE_DATE) {
			setEndSaveDate(date);
			onFilterChange({ endSaveDate: date });
		} else {
			setEndCreatedDate(date);
			onFilterChange({ endCreatedDate: date });
		}
	};

	const handleChangeStatus = (event) => {
		let newLst = [...event.target.value];
		newLst = newLst?.map((s) => {
			if (!s?.code) return statusList.find((tmps) => s === tmps.code);
			return s;
		});
		onFilterChange({ status: newLst.filter(Boolean) });
	};

	const handleChangeSortBy = (event) => {
		setSortBy(event.target.value);
		onFilterChange({ sortBy: event.target.value });
	};

	// Filtre « Intégré » (date de création). Il n'y a délibérément PAS d'équivalent « Exporté » :
	// « Exporté » n'existe pas dans le cycle de vie d'un avoir, et le paramètre est absent du backend.
	const handleChangeIntegratedFilter = (event) => {
		setSelectedPeriodIntegration(event.target.value);
		onFilterChange({ periodIntegrated: event.target.value });
	};

	useEffect(() => {
		loadAvoirStatuses();
	}, [loadAvoirStatuses]);

	useEffect(() => {
		if (filterInvoices) {
			setSearchField(filterInvoices.searchBy);
			setStartIssueDate(filterInvoices.startIssueDate);
			setStartSaveDate(filterInvoices.startSaveDate);
			setStartCreatedDate(filterInvoices.startCreatedDate);
			setEndIssueDate(filterInvoices.endIssueDate);
			setEndSaveDate(filterInvoices.endSaveDate);
			setEndCreatedDate(filterInvoices.endCreatedDate);
			setSortBy(filterInvoices.sortBy);
			setSelectedPeriodIntegration(filterInvoices.periodIntegrated || "");
		}
	}, [filterInvoices]);

	const debouncer = useMemo(
		() => debounce((nextValue) => onFilterChange({ searchBy: nextValue }), 500),
		[onFilterChange]
	);

	useEffect(() => () => debouncer.cancel(), [debouncer]);

	const handleFilterValueChange = (e) => {
		setSearchField(e);
		debouncer(e);
	};

	return (
		<Grid
			container
			style={{
				width: "99%",
			}}
		>
			<Grid container justify="center" spacing={1}>
				<Grid item xs="auto">
					<SearchFilter
						callback={(val) => handleFilterValueChange(val)}
						placeHolder={t("SearchAvoir")}
						style={classes.searchIcon}
						value={searchField != null ? searchField : ""}
					/>
				</Grid>
				<Grid item xs justify="center">
					<FormControl fullWidth>
						<InputLabel id="status">{t("Status")}</InputLabel>
						<Select
							labelId="status"
							id="statusId"
							multiple
							value={(filterInvoices?.status || []).map((s) => s.code)}
							onChange={handleChangeStatus}
							input={<Input />}
							renderValue={(selected) =>
								statusList
									?.filter((value) => selected?.includes(value?.code))
									?.map((s) => t(s?.label))
									.join(", ")
							}
						>
							{statusList.map((row) => (
								<MenuItem key={row.code} value={row.code}>
									<Checkbox
										checked={filterInvoices?.status?.map((s) => s?.code)?.indexOf(row.code) > -1}
									/>
									<ListItemText primary={t(row.label)} />
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				<Grid item xs justify="center">
					<AddressingKeyFilter
						value={filterInvoices?.addressingKey}
						onChange={(codes) => onFilterChange({ addressingKey: codes })}
					/>
				</Grid>
				<Grid item xs justify="center">
					<FormControl fullWidth>
						<InputLabel id="integrated">{t("Integrated")}</InputLabel>
						<Select
							labelId="integratedFilterPeriodId"
							id="integratedFilterByPeriod"
							value={selectedPeriodIntegration}
							input={<Input />}
							onChange={handleChangeIntegratedFilter}
							renderValue={(selected) => t(selected)}
						>
							{selectedPeriodIntegration && !isEmpty(selectedPeriodIntegration) && (
								<MenuItem value="">
									<Grid container direction="row" style={{ color: "gray" }}>
										<ListItemText primary="Enlever le filtre" />
										<CloseIcon />
									</Grid>
								</MenuItem>
							)}
							<MenuItem key="0" value="day">
								<ListItemText primary={t("day")} />
							</MenuItem>
							<MenuItem key="1" value="week">
								<ListItemText primary={t("week")} />
							</MenuItem>
							<MenuItem key="2" value="month">
								<ListItemText primary={t("month")} />
							</MenuItem>
							<MenuItem key="3" value="year">
								<ListItemText primary={t("year")} />
							</MenuItem>
						</Select>
					</FormControl>
				</Grid>
				<Grid item xs justify="center">
					<FormControl fullWidth>
						<InputLabel id="sortBy">{t("sortBy")}</InputLabel>
						<Select
							labelId="sortBy"
							id="sortById"
							value={sortBy}
							input={<Input />}
							onChange={handleChangeSortBy}
							renderValue={(selected) => t(selected)}
						>
							{!isEmpty(sortBy) && (
								<MenuItem value="">
									<Grid container direction="row" style={{ color: "gray" }}>
										<ListItemText primary="Enlever le filtre" />
										<CloseIcon />
									</Grid>
								</MenuItem>
							)}
							{columns.map((row, i) => (
								<MenuItem key={i} value={row}>
									<ListItemText primary={t(row)} />
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				<Grid item className={classes.filterEnd} alignItems="end" spacing={3}>
					<Tooltip title={t("dates_filter")} placement="top" arrow>
						<IconButton
							className={classes.dateFilters}
							onClick={(e) => setDateFilterAnchorEl(e.currentTarget)}
						>
							<DateRange />
						</IconButton>
					</Tooltip>
					<Popover
						id="filter-more-popover"
						open={Boolean(dateFilterAnchorEl)}
						anchorEl={dateFilterAnchorEl}
						onClose={() => setDateFilterAnchorEl(null)}
						anchorOrigin={{
							vertical: "top",
							horizontal: "right",
						}}
						transformOrigin={{
							vertical: "top",
							horizontal: "right",
						}}
						className={classes.dateFilterPopover}
					>
						<Box p={1}>
							<Typography className={classes.dateRangeLabel} gutterBottom>
								{`${t("issueDate")}:`}
							</Typography>
							<DateRangePicker
								start={startIssueDate ? new Date(startIssueDate) : ""}
								end={endIssueDate ? new Date(endIssueDate) : ""}
								onStartChange={(val) => handleStartDateChange(val, ISSUE_DATE)}
								onEndChange={(val) => handleEndDateChange(val, ISSUE_DATE)}
							/>
						</Box>
						<Box p={1}>
							<Typography className={classes.dateRangeLabel} gutterBottom>
								{`${t("createdDate")}:`}
							</Typography>
							<DateRangePicker
								start={startCreatedDate ? new Date(startCreatedDate) : ""}
								end={endCreatedDate ? new Date(endCreatedDate) : ""}
								onStartChange={(val) => handleStartDateChange(val, CREATE_DATE)}
								onEndChange={(val) => handleEndDateChange(val, CREATE_DATE)}
							/>
						</Box>
						<Box p={1}>
							<Typography className={classes.dateRangeLabel} gutterBottom>
								{`${t("saveDate")}:`}
							</Typography>
							<DateRangePicker
								start={startSaveDate ? new Date(startSaveDate) : ""}
								end={endSaveDate ? new Date(endSaveDate) : ""}
								onStartChange={(val) => handleStartDateChange(val, SAVE_DATE)}
								onEndChange={(val) => handleEndDateChange(val, SAVE_DATE)}
							/>
						</Box>
					</Popover>
				</Grid>
			</Grid>
		</Grid>
	);
}

const mapStateToProps = (state) => {
	const dataQueris = state.requests.queries;
	const statusResponse = get(dataQueris, "FETCH_AVOIR_STATUSES.data", []);
	const avoirStatusList = Array.isArray(statusResponse)
		? statusResponse
		: statusResponse?.invoiceStatusList || statusResponse?.avoirStatusList || [];
	return {
		invoices: dataQueris.LIST_FETCH_AVOIRS && dataQueris.LIST_FETCH_AVOIRS.data,
		statusList: Array.isArray(avoirStatusList) ? avoirStatusList : [],
	};
};
export default connect(mapStateToProps, { fetchAvoirs, fetchListStatus })(Filter);
